import { useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import { useCallback, useState } from 'react';

import {
  LimitedPartnerMappingForAliasingGQLType,
  useUpdateLimitedPartnerMappingMutation,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  successToastConfig,
  errorToastConfig,
} from '~/packages/common-frontend/constants';

export type AutoMatch = {
  mappingId: string;
  creditFacilityId: string;
  limitedPartnerId: string;
};

export type BulkAutoMatchResult = {
  total: number;
  succeeded: number;
  failed: number;
};

export type BulkAutoMatchProgress = {
  settled: number;
  total: number;
};

// The mutation updates one mapping per request, so a bulk match is N requests.
// Cap how many are in flight so matching across every facility doesn't flood
// the API.
const BULK_AUTO_MATCH_CONCURRENCY = 8;

/**
 * The auto-matches for a set of mappings: every still-unmatched mapping that
 * has an exact-name suggestion.
 */
export const toAutoMatches = (
  mappings: ReadonlyArray<
    Pick<
      LimitedPartnerMappingForAliasingGQLType,
      | 'mappingId'
      | 'creditFacilityId'
      | 'limitedPartnerId'
      | 'suggestedLimitedPartnerId'
    >
  >,
): AutoMatch[] =>
  mappings.flatMap((mapping) =>
    !mapping.limitedPartnerId && mapping.suggestedLimitedPartnerId
      ? [
          {
            mappingId: mapping.mappingId,
            creditFacilityId: mapping.creditFacilityId,
            limitedPartnerId: mapping.suggestedLimitedPartnerId,
          },
        ]
      : [],
  );

const pluralizeEntries = (count: number): string =>
  `${count} ${count === 1 ? 'entry' : 'entries'}`;

/**
 * Applies a batch of auto-matches with bounded concurrency, in the given
 * order, and reports the outcome in a single summary toast. `onMatched` fires
 * as each match succeeds and `progress` tracks the run, so callers can show
 * feedback while it's in flight. Callers refetch whatever they display once
 * the returned promise resolves.
 */
export const useBulkAutoMatch = (): {
  runBulkAutoMatch: (
    matches: AutoMatch[],
    onMatched: (match: AutoMatch) => void,
  ) => Promise<BulkAutoMatchResult>;
  progress: BulkAutoMatchProgress | null;
} => {
  const toast = useToast();
  const [
    updateLimitedPartnerMapping,
  ] = useUpdateLimitedPartnerMappingMutation();
  const [progress, setProgress] = useState<BulkAutoMatchProgress | null>(null);

  const runBulkAutoMatch = useCallback(
    async (
      matches: AutoMatch[],
      onMatched: (match: AutoMatch) => void,
    ): Promise<BulkAutoMatchResult> => {
      const total = matches.length;
      setProgress({ settled: 0, total });
      const queue = [...matches];
      let succeeded = 0;
      let failed = 0;

      // Each worker takes the next match off the shared queue until it's empty.
      const worker = async (): Promise<void> => {
        const next = queue.shift();
        if (!next) {
          return;
        }
        try {
          await updateLimitedPartnerMapping({
            variables: {
              limitedPartnerMappingId: next.mappingId,
              limitedPartnerId: next.limitedPartnerId,
            },
          });
          succeeded += 1;
          onMatched(next);
        } catch {
          failed += 1;
        }
        setProgress({ settled: succeeded + failed, total });
        await worker();
      };

      try {
        await Promise.all(
          Array.from(
            { length: Math.min(BULK_AUTO_MATCH_CONCURRENCY, total) },
            worker,
          ),
        );
      } finally {
        setProgress(null);
      }

      // A unique id per run: the shared configs carry a fixed id, and Chakra
      // drops a toast whose id is already on screen.
      const id = `auto-match-bulk-toast-${_.uniqueId()}`;
      if (failed === 0) {
        toast({
          ...successToastConfig,
          id,
          title: `Successfully auto-matched ${pluralizeEntries(total)}.`,
        });
      } else if (succeeded > 0) {
        toast({
          ...successToastConfig,
          id,
          title: `Auto-matched ${succeeded} of ${pluralizeEntries(
            total,
          )}. ${failed} failed.`,
        });
      } else {
        toast({
          ...errorToastConfig,
          id,
          title: `Auto-match failed for all ${pluralizeEntries(total)}.`,
        });
      }

      return { total, succeeded, failed };
    },
    [toast, updateLimitedPartnerMapping],
  );

  return { runBulkAutoMatch, progress };
};
